// src/components/TestracketsView.tsx
import React, { useState } from 'react';
import { Calendar, Plus, AlertTriangle, ArrowUpDown, Trash2 } from 'lucide-react';
import { collection, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, appId } from '../constants/firebase';
import { formatDateEU, formatAgendaDay } from '../utils/formatters';

interface TestracketsViewProps {
    user: any;
    testrackets: any[];
    customers: any[];
    openCustomerHistory: (customer: any) => void;
    createQuickCustomer: (name: string) => Promise<string | null>;
}

export default function TestracketsView({ 
    user, 
    testrackets, 
    customers, 
    openCustomerHistory, 
    createQuickCustomer 
}: TestracketsViewProps) {
    // --- LOKALE STATE (verhuisd uit App.tsx!) ---
    const [newTestracket, setNewTestracket] = useState({ brand: '', model: '', grip: '' });
    const [newRental, setNewRental] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() + 7);
        return { testracketId: '', customerId: '', returnDate: d.toISOString().split('T')[0] };
    });
    const [rentalCustomerSearch, setRentalCustomerSearch] = useState('');
    const [showRentalCustomerDropdown, setShowRentalCustomerDropdown] = useState(false);
    const [testracketsSortConfig, setTestracketsSortConfig] = useState({ key: 'racket', direction: 'ascending' });

    // --- LOKALE FUNCTIES (verhuisd uit App.tsx!) ---
    const getCustomerName = (id: string) => {
        const c = customers.find(c => c.id === id);
        return c ? c.name : 'Onbekend';
    };

    const requestTestracketsSort = (key: string) => {
        let direction = 'ascending';
        if (testracketsSortConfig.key === key && testracketsSortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setTestracketsSortConfig({ key, direction });
    };

    const addTestracket = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !newTestracket.brand || !newTestracket.model) return;
        try {
            await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'testrackets'), {
                ...newTestracket, status: 'available', createdAt: Date.now()
            });
            setNewTestracket({ brand: '', model: '', grip: '' });
        } catch (err) { console.error(err); alert("Kon testracket niet opslaan"); }
    };

    const deleteTestracket = async (id: string) => {
        if (!user) return;
        if (!window.confirm("Weet je zeker dat je dit testracket wilt verwijderen?")) return;
        try { await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'testrackets', id)); } 
        catch (err) { console.error(err); }
    };

    const selectCustomerForRental = (customer: any) => {
        setNewRental({ ...newRental, customerId: customer.id });
        setRentalCustomerSearch(customer.name);
        setShowRentalCustomerDropdown(false);
    };

    const rentTestracket = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !newRental.testracketId || !newRental.customerId || !newRental.returnDate) {
            alert("Vul alle velden in om een testracket uit te lenen."); return;
        }
        try {
            const startDate = new Date().toISOString().split('T')[0];
            await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'testrackets', newRental.testracketId), {
                status: 'rented',
                currentRental: { customerId: newRental.customerId, startDate: startDate, returnDate: newRental.returnDate }
            });

            const customer = customers.find(c => c.id === newRental.customerId);
            const racket = testrackets.find(r => r.id === newRental.testracketId);
            if (customer && racket) {
                const currentHistory = customer.testHistory || [];
                await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'customers', customer.id), {
                    testHistory: [{
                        id: Date.now(), racketId: racket.id, brand: racket.brand, model: racket.model,
                        startDate: startDate, returnDate: newRental.returnDate
                    }, ...currentHistory]
                });
            }

            const d = new Date(); d.setDate(d.getDate() + 7);
            setNewRental({ testracketId: '', customerId: '', returnDate: d.toISOString().split('T')[0] });
            setRentalCustomerSearch('');
        } catch (err) { console.error(err); alert("Kon verhuur niet opslaan"); }
    };

    const returnTestracket = async (id: string) => {
        if (!user) return;
        if (!window.confirm("Is dit racket succesvol teruggebracht?")) return;
        try {
            await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'testrackets', id), {
                status: 'available', currentRental: null
            });
        } catch (err) { console.error(err); }
    };

    // --- WEERGAVE LOGICA ---
    const todayStr = new Date().toISOString().split('T')[0];
    const agendaDays = Array.from({length: 7}).map((_, i) => {
        const d = new Date(); d.setDate(d.getDate() + i); return d.toISOString().split('T')[0];
    });

    const overdueRackets = testrackets.filter(r => r.status === 'rented' && r.currentRental?.returnDate < todayStr);

    let sortedTestrackets = [...testrackets];
    sortedTestrackets.sort((a, b) => {
        let valA = '', valB = '';
        if (testracketsSortConfig.key === 'racket') {
            valA = `${a.brand} ${a.model}`.toLowerCase(); valB = `${b.brand} ${b.model}`.toLowerCase();
        } else if (testracketsSortConfig.key === 'status') {
            valA = a.status; valB = b.status;
        } else if (testracketsSortConfig.key === 'customer') {
            valA = (a.status === 'rented' && a.currentRental) ? getCustomerName(a.currentRental.customerId).toLowerCase() : '';
            valB = (b.status === 'rented' && b.currentRental) ? getCustomerName(b.currentRental.customerId).toLowerCase() : '';
        } else {
            valA = (a[testracketsSortConfig.key] || '').toString().toLowerCase();
            valB = (b[testracketsSortConfig.key] || '').toString().toLowerCase();
        }
        if (valA < valB) return testracketsSortConfig.direction === 'ascending' ? -1 : 1;
        if (valA > valB) return testracketsSortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
    });

    const exactMatchRental = customers.some(c => c.name.toLowerCase() === rentalCustomerSearch.toLowerCase().trim());

    return (
        <div className="space-y-8 animate-in fade-in duration-500 max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center">
                <Calendar className="w-6 h-6 mr-3 text-indigo-600 dark:text-indigo-400" />
                Testrackets & Agenda
            </h2>

            {/* SECTIE 1: RACKET UITLENEN */}
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="font-bold text-lg mb-4 text-slate-800 dark:text-white">Nieuwe Uitleen</h3>
                <form onSubmit={rentTestracket} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    
                    <div className="relative md:col-span-1">
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Selecteer Klant</label>
                        <input 
                            type="text" placeholder="Zoek klant..." 
                            className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white" 
                            value={rentalCustomerSearch} 
                            onChange={(e) => { setRentalCustomerSearch(e.target.value); setShowRentalCustomerDropdown(true); setNewRental({...newRental, customerId: ''}); }} 
                            onFocus={() => setShowRentalCustomerDropdown(true)} 
                            onBlur={() => setTimeout(() => setShowRentalCustomerDropdown(false), 200)} 
                        />
                        {showRentalCustomerDropdown && rentalCustomerSearch && (
                            <ul className="absolute z-20 w-full bg-white dark:bg-slate-800 mt-1 rounded-xl shadow-lg border border-slate-100 dark:border-slate-700 max-h-48 overflow-y-auto">
                                {!exactMatchRental && rentalCustomerSearch.trim() !== '' && (
                                    <li 
                                        onMouseDown={async (e) => {
                                            e.preventDefault();
                                            const id = await createQuickCustomer(rentalCustomerSearch);
                                            if(id) {
                                                setNewRental({ ...newRental, customerId: id });
                                                setRentalCustomerSearch(rentalCustomerSearch.trim());
                                                setShowRentalCustomerDropdown(false);
                                            }
                                        }}
                                        className="px-4 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 cursor-pointer flex items-center border-b border-slate-50 dark:border-slate-700 text-indigo-600 dark:text-indigo-400 font-bold text-sm"
                                    >
                                        <Plus className="w-4 h-4 mr-2" /> Voeg "{rentalCustomerSearch}" toe als klant
                                    </li>
                                )}
                                {customers.filter(c => c.name.toLowerCase().includes(rentalCustomerSearch.toLowerCase())).map(c => (
                                    <li key={c.id} onMouseDown={(e) => { e.preventDefault(); selectCustomerForRental(c); }} className="px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer border-b border-slate-50 dark:border-slate-700 text-sm">{c.name}</li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="md:col-span-1">
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Beschikbaar Racket</label>
                        <select 
                            className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white"
                            value={newRental.testracketId} onChange={(e) => setNewRental({...newRental, testracketId: e.target.value})}
                        >
                            <option value="">-- Kies Racket --</option>
                            {testrackets.filter(r => r.status === 'available').map(r => (
                                <option key={r.id} value={r.id}>{r.brand} {r.model} {r.grip ? `(Grip ${r.grip})` : ''}</option>
                            ))}
                        </select>
                    </div>

                    <div className="md:col-span-1">
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Retour Datum (+7 dgn)</label>
                        <input 
                            type="date" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white"
                            value={newRental.returnDate} onChange={(e) => setNewRental({...newRental, returnDate: e.target.value})}
                        />
                    </div>

                    <div className="md:col-span-1">
                        <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 transition text-white py-3 rounded-xl font-bold text-sm">Bevestig Uitleen</button>
                    </div>
                </form>
            </div>

            {/* SECTIE 2: AGENDA VIEW */}
            <div>
                <h3 className="font-bold text-lg mb-4 text-slate-800 dark:text-white">Agenda (Komende 7 dagen)</h3>
                
                {overdueRackets.length > 0 && (
                    <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                        <h4 className="font-bold text-red-800 dark:text-red-300 flex items-center mb-2">
                            <AlertTriangle className="w-4 h-4 mr-2" /> Te Laat Teruggebracht ({overdueRackets.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {overdueRackets.map(racket => (
                                <div key={racket.id} className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded-lg border border-red-100 dark:border-red-900/50">
                                    <div>
                                        <p className="text-sm font-bold text-slate-800 dark:text-white">{racket.brand} {racket.model}</p>
                                        <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                                            Klant: <span className="cursor-pointer hover:underline" onClick={() => { const c = customers.find(cust => cust.id === racket.currentRental?.customerId); if (c) openCustomerHistory(c); }}>{getCustomerName(racket.currentRental?.customerId)}</span>
                                        </p>
                                        <p className="text-xs text-slate-500">Geleend op: {formatDateEU(racket.currentRental?.startDate)}</p>
                                    </div>
                                    <button onClick={() => returnTestracket(racket.id)} className="px-3 py-1 bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 rounded-lg text-xs font-bold hover:bg-red-200 dark:hover:bg-red-800 transition">
                                        Ingeleverd
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
                    {agendaDays.map((day, index) => {
                        const dayRackets = testrackets.filter(r => r.status === 'rented' && r.currentRental?.returnDate === day);
                        const isToday = index === 0;

                        return (
                            <div key={day} className={`bg-white dark:bg-slate-800 rounded-2xl p-4 border flex flex-col min-h-[150px] shadow-sm ${isToday ? 'border-indigo-500 dark:border-indigo-400 bg-indigo-50/30 dark:bg-indigo-900/10' : 'border-slate-200 dark:border-slate-700'}`}>
                                <div className={`font-bold text-sm border-b pb-2 mb-3 ${isToday ? 'text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800' : 'text-slate-800 dark:text-white border-slate-100 dark:border-slate-700'}`}>
                                    {isToday ? 'Vandaag' : formatAgendaDay(day)}
                                </div>
                                <div className="flex-1 space-y-2 relative">
                                    {dayRackets.length === 0 ? (
                                        <p className="text-xs text-slate-400 italic">Geen retouren</p>
                                    ) : (
                                        dayRackets.map(racket => (
                                            <div key={racket.id} className="relative group bg-slate-50 dark:bg-slate-900 p-2 rounded-lg border border-slate-100 dark:border-slate-700">
                                                <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{racket.brand} {racket.model}</p>
                                                <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-medium truncate mb-2 cursor-pointer hover:underline" onClick={() => { const c = customers.find(cust => cust.id === racket.currentRental?.customerId); if (c) openCustomerHistory(c); }}>
                                                    {getCustomerName(racket.currentRental?.customerId)}
                                                </p>
                                                <button onClick={() => returnTestracket(racket.id)} className="w-full px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded text-[10px] font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition">
                                                    Check in
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* SECTIE 3: BEHEER ALLE TESTRACKETS */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h3 className="font-bold text-lg text-slate-800 dark:text-white">Beheer Testrackets ({sortedTestrackets.length})</h3>
                    <form onSubmit={addTestracket} className="flex w-full md:w-auto gap-2">
                        <input type="text" placeholder="Merk" className="w-24 p-2 bg-slate-50 dark:bg-slate-900 border-none rounded-lg text-sm text-slate-900 dark:text-white" value={newTestracket.brand} onChange={e => setNewTestracket({...newTestracket, brand: e.target.value})} required />
                        <input type="text" placeholder="Model" className="w-32 p-2 bg-slate-50 dark:bg-slate-900 border-none rounded-lg text-sm text-slate-900 dark:text-white" value={newTestracket.model} onChange={e => setNewTestracket({...newTestracket, model: e.target.value})} required />
                        <input type="text" placeholder="Grip" className="w-16 p-2 bg-slate-50 dark:bg-slate-900 border-none rounded-lg text-sm text-slate-900 dark:text-white" value={newTestracket.grip} onChange={e => setNewTestracket({...newTestracket, grip: e.target.value})} />
                        <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-lg"><Plus className="w-4 h-4" /></button>
                    </form>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-slate-600 dark:text-slate-300">
                        <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-900/50">
                            <tr>
                                <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestTestracketsSort('racket')}>Racket <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestTestracketsSort('status')}>Status <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestTestracketsSort('customer')}>Klant & Uitleen Info <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                <th className="px-6 py-3 text-right">Actie</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {sortedTestrackets.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-400">Nog geen testrackets toegevoegd.</td></tr>}
                            {sortedTestrackets.map(racket => (
                                <tr key={racket.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                    <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">
                                        {racket.brand} {racket.model} {racket.grip ? <span className="text-xs text-slate-400 ml-1">(Grip {racket.grip})</span> : ''}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${racket.status === 'available' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'}`}>
                                            {racket.status === 'available' ? 'Beschikbaar' : 'Uitgeleend'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-xs">
                                        {racket.status === 'rented' && racket.currentRental ? (
                                            <div>
                                                <p className="font-bold text-slate-800 dark:text-slate-200 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors inline-block" onClick={() => { const c = customers.find(cust => cust.id === racket.currentRental.customerId); if (c) openCustomerHistory(c); }}>
                                                    {getCustomerName(racket.currentRental.customerId)}
                                                </p>
                                                <p className="text-slate-500 dark:text-slate-400">Vanaf: {formatDateEU(racket.currentRental.startDate)} • Tot: {formatDateEU(racket.currentRental.returnDate)}</p>
                                            </div>
                                        ) : <span className="text-slate-400">-</span>}
                                    </td>
                                    <td className="px-6 py-4 text-right flex justify-end gap-2">
                                        {racket.status === 'rented' && (
                                            <button onClick={() => returnTestracket(racket.id)} className="p-2 text-green-600 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 rounded-lg transition text-xs font-bold">Ingeleverd</button>
                                        )}
                                        <button onClick={() => deleteTestracket(racket.id)} className="p-2 text-red-500 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-lg transition">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}